import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Plus, Bot } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import MatrixDetail from "@/components/kpi/MatrixDetail";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    {children}
  </div>
);

export const MATRIX_STATUS_CHIP = {
  ai_draft: "border border-amber-500 text-amber-700",
  active: "bg-teal-600 text-white",
};

export default function RolesPage() {
  const { user } = useAuth();
  const [roles, setRoles] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({ title: "", department: "", seniority: "mid", skills: "" });
  const [generating, setGenerating] = useState(false);
  const canManage = ["admin", "hr_manager"].includes(user.role);
  const allowed = canManage || user.role === "dept_head";

  const load = () => api.get("/kpi/roles").then((r) => { setRoles(r.data.roles); setBackfill(r.data.backfill_job); });
  useEffect(() => {
    if (!allowed) return;
    load();
    api.get("/employees/departments").then((r) => setDepartments(r.data.departments)).catch(() => {});
  }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // poll while migration backfill is running
  useEffect(() => {
    if (backfill?.status !== "running") return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [backfill?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) {
    return (
      <div className="border border-border bg-white p-10 text-center" data-testid="roles-restricted">
        <Lock className="h-6 w-6 mx-auto text-muted-foreground mb-3" />
        <p className="text-[14px] text-slate-700">Roles & KPI Matrices require an HR, admin or department-head role.</p>
      </div>
    );
  }

  const createRole = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/kpi/roles", {
        title: form.title, department: form.department, seniority: form.seniority,
        required_skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Agent generated a ${r.data.kpis.length}-KPI draft matrix for "${r.data.role_title}"`);
      setOpen(false);
      setForm({ title: "", department: "", seniority: "mid", skills: "" });
      await load();
      setSelected(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  if (selected) return <MatrixDetail matrixId={selected} onBack={() => { setSelected(null); load(); }} />;
  if (!roles) return <Skeleton className="h-96 w-full rounded-none" />;

  return (
    <div className="space-y-4 fade-in" data-testid="roles-page">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-muted-foreground max-w-2xl">
          Every role carries an AI-generated KPI matrix (5-8 weighted KPIs mapped to real system data).
          New roles trigger the agentic pipeline automatically — no blank forms.
        </p>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-role-btn" className="rounded-none bg-[var(--brand-primary)] hover:bg-[#0f172a] h-9 text-[12px]">
                <Plus className="h-4 w-4" /> New Job Role
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader><DialogTitle className="text-[15px]">Create a job role</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Field label="Role title">
                  <Input data-testid="role-title-input" value={form.title} placeholder="Solutions Architect"
                    onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Department">
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                      <SelectTrigger className="rounded-none" data-testid="role-dept-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {departments.map((d) => <SelectItem key={d} value={d} className="text-[12px]">{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Seniority">
                    <Select value={form.seniority} onValueChange={(v) => setForm({ ...form, seniority: v })}>
                      <SelectTrigger className="rounded-none" data-testid="role-seniority-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {["junior", "mid", "senior"].map((s) => <SelectItem key={s} value={s} className="text-[12px]">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Required skills (comma-separated)">
                  <Input data-testid="role-skills-input" value={form.skills}
                    placeholder="Cloud Architecture, FastAPI, Security Fundamentals"
                    onChange={(e) => setForm({ ...form, skills: e.target.value })} className="rounded-none" />
                </Field>
                {generating && (
                  <div className="flex items-center gap-2 text-[12px] text-teal-700 bg-teal-50 border border-teal-600/30 px-3 py-2">
                    <Bot className="h-4 w-4 animate-pulse" /> Agent gathering context → masked gateway → validating matrix… (~15s)
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="role-create-submit" onClick={createRole}
                  disabled={generating || form.title.trim().length < 3 || !form.department}
                  className="rounded-none bg-teal-600 hover:bg-teal-700">
                  {generating ? "Generating…" : "Create & generate matrix"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {backfill?.status === "running" && (
        <div className="bg-teal-50 border border-teal-600/40 px-4 py-2.5 text-[12px] text-teal-900" data-testid="backfill-banner">
          Migration backfill in progress — {backfill.done}/{backfill.total} role matrices generated…
        </div>
      )}

      <div className="bg-white border border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Role", "Department", "Seniority", "Status", "Employees mapped", "Provenance", ""].map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {roles.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`role-row-${m.id}`}
                onClick={() => setSelected(m.id)}>
                <td className="px-4 py-3 font-medium text-[var(--brand-primary)]">{m.role_title}</td>
                <td className="px-4 py-3 text-slate-600">{m.department}</td>
                <td className="px-4 py-3 text-slate-500 text-[12px]">{m.seniority}</td>
                <td className="px-4 py-3">
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${MATRIX_STATUS_CHIP[m.status]}`}>
                    {m.status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono">{m.employees_mapped}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono text-slate-400">
                    {m.provenance?.model} · audit {String(m.provenance?.audit_id).slice(0, 8)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-[11px] text-teal-700 font-medium">Open →</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
